import React, { useState, useEffect } from "react";
import { Flex, Text, Card, Switch, TextField, Button, Badge, IconButton, DropdownMenu, Dialog, Checkbox, Separator, 
  Select, Box, HoverCard, Tooltip } from "@radix-ui/themes";
import { useGetWirelessLAN, useUpdateWirelessLAN, useLiveAccessPoints } from "hooks";
import { useProfileContext, useLocationContext } from "context";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query"; // Import useQueryClient
import { DownloadIcon, CopyIcon, ArrowLeftIcon } from "@radix-ui/react-icons";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { ToastAlert } from "components";

const Networks = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { liveAccessPointsStatus } = useLiveAccessPoints(locationsData);
  const organisationID = userProfile.organisation_id;
  const { appearance } = useTheme();
  const routerLocation = useRouterLocation();
  const { setToastMessage } = useToast();

  const { isBrand = false, brandId, brandName, locationId, locationName, firstLocationId } = routerLocation.state || {};

  // Handle back button
  const handleBack = () => {
    navigate("/networks-selector");
  };

  const getQrBackgroundColor = () => {
    return appearance === "dark" ? "1a191c" : "ffffff";
  };

  const getQrUrl = (data, color = "666666", format = "png") => {
    return `https://api.qrserver.com/v1/create-qr-code/?data=${data}&size=128x128&bgcolor=${getQrBackgroundColor()}&color=${color}&format=${format}`;
  };

  // Grabbing the WLAN Data using the useGetWirelessLAN hook
  const { data: wirelessLanData } = useGetWirelessLAN(organisationID);

  // Handles dirty changes in the Networks Tab
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Call the hook at the component level, and get the function to execute the mutation
  const updateWirelessLAN = useUpdateWirelessLAN();

  // Creates the state that tracks changes in the network name, password.
  // Also creates the state that checks if the passworld field is visible, and if the save changes button is enabled or disabled.
  const [networkPageState, setNetworkPageState] = useState([]);
  const [interfaceState, setInterfaceState] = useState([]);

  // Creates the state that tracks the colour of the QR code
  // Also creates the state that checks to see if the "I understand that..." checkbox is ticked off!
  const [colourSelection, setColourSelection] = useState("666666");
  
  const [wifiDisclaimer, setWifiDisclaimer] = useState(false);

  const [filteredNetworks, setFilteredNetworks] = useState([]);

  const [selectedLocationId, setSelectedLocationId] = useState(null);

  // Add a state to track password modifications by location
  const [locationPasswords, setLocationPasswords] = useState({});

  // Get all locations for a brand
  const getBrandLocations = (brandId, networks) => {
    if (!brandId || !networks) return [];
    // Get all unique locations from networks with this brand ID
    const locationIds = networks
      .filter((network) => {
        const networkBrandId = typeof network.brand_id === "string" ? parseInt(network.brand_id, 10) : network.brand_id;
        const filterBrandId = typeof brandId === "string" ? parseInt(brandId, 10) : brandId;
        // Only include networks that have a valid location_id (not null or undefined)
        return networkBrandId === filterBrandId && network.location_id != null;
      })
      .map((network) => network.location_id);

    // Remove duplicate IDs
    const uniqueLocationIds = [...new Set(locationIds)];

    // Map to location objects using the locations context
    const brandLocations = uniqueLocationIds.map((locationId) => {
      // Skip null or undefined locationIds
      if (locationId == null) return null;
      
      // Parse IDs to make sure comparison works
      const parsedLocationId = parseInt(locationId, 10);

      // Find location in context by ID
      const contextLocation = locationsData ? locationsData.find((loc) => parseInt(loc.id, 10) === parsedLocationId) : null;

      // Get network for this location to access password
      const networkForLocation = networks.find((net) => parseInt(net.location_id, 10) === parsedLocationId && net.type === "Staff Wi-Fi Network");

      return {
        id: locationId,
        // Use context name if available, fallback to other options
        name: contextLocation ? contextLocation.name : networkForLocation?.location_name || networkForLocation?.location || `Location ${locationId}`,
        password: networkForLocation?.x_passphrase || "",
      };
    }).filter(location => location !== null); // Filter out any null entries

    return brandLocations.sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    if (wirelessLanData?.data) {
      let filtered = [];

      if (isBrand && brandId) {
        // Filter networks for this brand
        const brandNetworks = wirelessLanData.data.filter((network) => {
          const networkBrandId = typeof network.brand_id === "string" ? parseInt(network.brand_id, 10) : network.brand_id;
          const filterBrandId = typeof brandId === "string" ? parseInt(brandId, 10) : brandId;
          return networkBrandId === filterBrandId;
        });

        // Deduplicate networks by type for brand view (only show one network per type)
        // This ensures we only see one Guest, one Work, and one Staff network
        const networkTypes = new Set(brandNetworks.map((network) => network.type));

        // For each network type, take the first network with that type
        networkTypes.forEach((type) => {
          const networkOfType = brandNetworks.find((network) => network.type === type);
          if (networkOfType) {
            filtered.push(networkOfType);
          }
        });
      } else if (locationId) {
        // Filter networks for this location
        filtered = wirelessLanData.data.filter((network) => {
          const networkLocationId = typeof network.location_id === "string" ? parseInt(network.location_id, 10) : network.location_id;
          const filterLocationId = typeof locationId === "string" ? parseInt(locationId, 10) : locationId;
          return networkLocationId === filterLocationId;
        });
      } else {
        // No filtering (fallback)
        filtered = wirelessLanData.data;
      }

      setFilteredNetworks(filtered);

      // Initialize state with the filtered data
      const initialNetworkPageState = filtered.map((entry, index) => ({
        id: index,
        enabled: entry.enabled,
        name: entry.name,
        x_passphrase: entry.x_passphrase,
      }));

      const initialInterfaceState = filtered.map((_, index) => ({
        id: index,
        disabled: true,
        textfield_type: "password",
      }));

      setNetworkPageState(initialNetworkPageState);
      setInterfaceState(initialInterfaceState);
      setIsLoading(false);
    }
  }, [wirelessLanData, isBrand, brandId, locationId]);

  // Creates the event handlers that will be used to update the network name and password
  const handleChanges = (event, id) => {
    // Sets the state of the textfield type to "text" if the password field is focused, and "password" if the password field is blurred.
    setInterfaceState((prevInterfaceState) =>
      prevInterfaceState.map((item) => {
        if (item.id === id) {
          let updatedItem = { ...item };
          if (event.type === "focus") {
            updatedItem.textfield_type = "text";
          } else if (event.type === "blur") {
            updatedItem.textfield_type = "password";
          } else if (event.type === "change") {
            updatedItem.disabled = false;
          }
          return updatedItem;
        }
        return item;
      })
    );

    if (event.type === "change") {
      // Sets the state of the "Save Changes" button to be enabled
      setInterfaceState((prevInterfaceState) => prevInterfaceState.map((item) => (item.id === id ? { ...item, disabled: false } : item)));

      // Sets the state of the network page name and password
      setNetworkPageState((prevNetworkPageState) => prevNetworkPageState.map((item) => (item.id === id ? { ...item, [event.target.name]: event.target.value } : item)));
      setIsDirty(true);
    }
  };

  const getLocationIdsForUpdate = () => {
    // For brand update: collect all unique location IDs from filtered networks
    if (isBrand && brandId) {
      // Extract all unique location IDs from the filtered networks
      const locationIds = [...new Set(filteredNetworks.map((network) => network.location_id))];
      return locationIds;
    }
    // For independent location update: use the single location ID
    else if (locationId) {
      return [locationId];
    }
    // Fallback (shouldn't happen, but just in case)
    return [];
  };

  useEffect(() => {
    // Initialize selected location when data is loaded
    if (wirelessLanData?.data && isBrand && brandId) {
      const locations = getBrandLocations(brandId, wirelessLanData.data);
      if (locations.length > 0) {
        setSelectedLocationId(locations[0].id);
      }
    } else if (locationId) {
      setSelectedLocationId(locationId);
    }
  }, [wirelessLanData?.data, isBrand, brandId, locationId]);

  // Handle location selection change for QR code
  const handleLocationChange = (locationId) => {
    setSelectedLocationId(locationId);
  };

  // Handle password change for a specific location
  const handlePasswordChange = (event, networkIndex, locationId) => {
    const value = event.target.value;

    // Update locationPasswords state
    setLocationPasswords((prev) => ({
      ...prev,
      [locationId]: {
        ...prev[locationId],
        value,
        networkIndex,
        modified: true,
      },
    }));

    // Enable the save button
    setInterfaceState((prev) => prev.map((item) => (item.id === networkIndex ? { ...item, disabled: false } : item)));

    setIsDirty(true);
  };

  const getWlanTypeId = (networkType) => {
    switch(networkType) {
      case "Guest Wi-Fi Network":
        return 0;
      case "Work Wi-Fi Network": 
        return 1;
      case "Staff Wi-Fi Network":
        return 2;
      default:
        return null;
    }
  };

  const sendPayload = async (checkedOrEvent, id) => {
    const network = filteredNetworks[id];

    if (!network) {
      console.error("Missing network data");
      return;
    }
    const wlanTypeId = getWlanTypeId(network.type);
    try {
      if (typeof checkedOrEvent === "boolean") {
        // Update network enabled state (keep existing behavior)
        setNetworkPageState((prevState) => prevState.map((item) => (item.id === id ? { ...item, enabled: checkedOrEvent } : item)));

        // For enable/disable, still apply to all locations
        const location_ids = getLocationIdsForUpdate();

        await updateWirelessLAN.mutateAsync({
          organisationID,
          data: {
            id: wlanTypeId,
            payload: {
              enabled: checkedOrEvent,
            },
            location_ids,
            request_type: "toggle_wlan",
            wlan_type: network.type,
            brand_id: isBrand ? brandId : null,
          },
        });
        queryClient.invalidateQueries(["wireless-lan", organisationID]);
        setToastMessage(`${network.type} ${checkedOrEvent ? "enabled" : "disabled"}`);
      } else if (typeof checkedOrEvent === "object") {
        // For button clicks (save changes)
        const changes = networkPageState.find((item) => item.id === id);
        if (!changes) return;

        // Check what type of update we're doing
        if (isBrand && network.type === "Staff Wi-Fi Network") {
          // Staff network in brand view - check if we have modified passwords
          const modifiedLocations = Object.keys(locationPasswords).filter((locId) => locationPasswords[locId].networkIndex === id && locationPasswords[locId].modified);

          if (modifiedLocations.length > 0) {
            // We have modified passwords - update each one separately
            for (const locId of modifiedLocations) {
              await updateWirelessLAN.mutateAsync({
                organisationID,
                data: {
                  id: wlanTypeId,
                  payload: {
                    // Only sending name and enabled status, but not changing them
                    name: changes.name,
                    enabled: changes.enabled,
                    x_passphrase: locationPasswords[locId].value,
                  },
                  location_ids: [parseInt(locId, 10)],
                  request_type: "update_wlan",
                  wlan_type: network.type,
                  brand_id: isBrand ? brandId : null,
                },
              });
            }
            setToastMessage("Changed saved")
          } else {
            // Just updating the name for all locations
            const location_ids = getLocationIdsForUpdate();
            await updateWirelessLAN.mutateAsync({
              organisationID,
              data: {
                id: wlanTypeId,
                payload: {
                  name: changes.name,
                  enabled: changes.enabled,
                },
                location_ids,
                request_type: "update_wlan",
                wlan_type: network.type,
                brand_id: isBrand ? brandId : null,
              },
            });
            setToastMessage("Changed saved")
          }
        } else {
          // Regular update (single location or non-staff network)
          const location_ids = getLocationIdsForUpdate();
          await updateWirelessLAN.mutateAsync({
            organisationID,
            data: {
              id: wlanTypeId,
              payload: {
                name: changes.name,
                x_passphrase: changes.x_passphrase,
                enabled: changes.enabled,
              },
              location_ids,
              request_type: "update_wlan",
              wlan_type: network.type,
              brand_id: isBrand ? brandId : null,
            },
          });
          setToastMessage("Changed saved")
        }

        // Invalidate query to refresh data
        queryClient.invalidateQueries(["wireless-lan", organisationID]);
      }

      // Reset states after successful update
      setNetworkPageState((prevState) => prevState.map((item) => (item.id === id ? { id } : item)));

      setInterfaceState((prevState) => prevState.map((item) => (item.id === id ? { ...item, disabled: true } : item)));

      // Clear modified passwords
      setLocationPasswords({});
      setWifiDisclaimer(false);
    } catch (error) {
      console.error("Failed to update network:", error);
      setToastMessage("Failed to update network");
    }
  };

  const getLocationPassword = (network, locationId) => {
    // Safety check - if no locationId is provided
    if (!locationId) return network?.x_passphrase || "";

    // If we have a modified password for this location, use that
    const modifiedPassword = locationPasswords[locationId];
    if (modifiedPassword && modifiedPassword.networkIndex === network.id) {
      return modifiedPassword.value || "";
    }

    // Otherwise use the stored password from data
    if (isBrand && brandId) {
      // Find the network for this specific location
      const locationNetwork = wirelessLanData?.data?.find((net) => net.type === network.type && parseInt(net.location_id, 10) === parseInt(locationId, 10));
      return locationNetwork?.x_passphrase || network?.x_passphrase || "";
    }

    return network?.x_passphrase || "";
  };

  // Creates the event handlers that will be used to download the QR code.
  const handleDownload = (url, filename) => {
    return async () => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error("Download failed:", error);
      }
    };
  };

  // Creates the event handlers that will be used to copy the QR code.
  // Add this function at the component level
  const handleCopy = (url) => {
    return async () => {
      try {
        // Fetch the image
        const response = await fetch(url);
        const blob = await response.blob();

        // Create a ClipboardItem
        const item = new ClipboardItem({
          [blob.type]: blob,
        });

        // Copy to clipboard
        await navigator.clipboard.write([item]);

        // Optional: Show success message
        console.log("QR code copied to clipboard");
      } catch (error) {
        console.error("Failed to copy QR code:", error);
      }
    };
  };

  const handleColourField = (event) => {
    if (event.target.value.slice(0, 1) === "#") {
      setColourSelection(event.target.value.slice(1, event.target.value.length));
    } else {
      setColourSelection(event.target.value);
    }
  };

  const getQrForegroundColor = () => {
    return appearance === "dark" ? "1a191c" : "ffffff";
  };

  useEffect(() => {
    if (interfaceState.every((item) => item.disabled === true)) {
      setIsDirty(false);
    }
  }, [interfaceState]);

  return (
    <Flex direction="column" gap="5" p="5" style={{ alignSelf: "stretch", width: "100%", height: "100%" }}>
      {/* Header with back button and context information */}
      <Flex gap="4" style={{ alignItems: "center" }}>
        <IconButton variant="soft" color="gray" onClick={handleBack}>
          <ArrowLeftIcon width={16} height={16} />
        </IconButton>
        <Flex direction="column" gap="1">
          <Text size="2" weight="medium">
            {isBrand ? `${brandName}` : locationName ? `${locationName}` : "Networks"}
          </Text>

          {/* Subtitle: Location count for brands or address for independent locations */}
          {isBrand && brandId ? (
            <React.Fragment>
              {getBrandLocations(brandId, wirelessLanData?.data).length > 0 ? (
              <HoverCard.Root>
                <HoverCard.Trigger>
                  <Text size="1" color="gray">
                    {getBrandLocations(brandId, wirelessLanData?.data).length === 1 
                      ? `${getBrandLocations(brandId, wirelessLanData?.data).length} location`
                      : `${getBrandLocations(brandId, wirelessLanData?.data).length} locations` 
                    }
                  </Text>
                </HoverCard.Trigger>
                <HoverCard.Content maxHeight="300px" style={{padding: 'var(--space-3)'}}>
                  <Flex direction="column" gap="3">
                    <Flex direction="column" gap="2">
                      {getBrandLocations(brandId, wirelessLanData?.data).map((location) => {
                        // Pull in live AP counts (fall back to static if needed)
                        const locationDetail = locationsData?.find((loc) => loc.id === location.id);
                        const liveStatus = liveAccessPointsStatus?.find((l) => l.name === location.name);
                        const onlineCount = liveStatus?.online_access_points_count ?? locationDetail?.online_access_points_count ?? 0;
                        const offlineCount = liveStatus?.offline_access_points_count ?? locationDetail?.offline_access_points_count ?? 0;
                        const isOnline = onlineCount > 0 && offlineCount === 0;
                        const isPartial = onlineCount > 0 && offlineCount > 0;
                        
                        // Set status color
                        let statusColor = "var(--red-9)"; // partial
                        if (isOnline) statusColor = "var(--grass-9)";
                        if (isPartial) statusColor = "var(--amber-9)";

                        return (
                          <Flex key={location.id} direction="column" gap="1">
                            <Flex align="center" gap="1">
                              <Tooltip content={isOnline ? "Online" : isPartial ? "Partially online" : "Offline"}>
                                <Box style={{ background: statusColor, height: "8px", width: "8px", borderRadius: "4px"}}/>
                              </Tooltip>
                              <Text size="1" weight="medium">{location.name}</Text>
                            </Flex>
                            <Text size="1" color="gray">{locationDetail?.address || "No address"}</Text>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </Flex>
                </HoverCard.Content>
              </HoverCard.Root>
            ) : (
              <Text size="1" color="gray">0 locations</Text>
            )}
            </React.Fragment>
          ) : locationId ? (
            <Text size="1" color="gray" style={{ maxWidth: "400px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
              {locationsData?.find((loc) => loc.id === locationId)?.address || "No address"}
            </Text>
          ) : null}
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex align="center" justify="center" style={{ height: "300px" }}>
          <Text>Loading network settings...</Text>
        </Flex>
      ) : (
        <React.Fragment>
          <Flex className="main-content-container" style={{ alignItems: "flex-start", flex: "1 0 0", alignSelf: "stretch", 
            overflow: "auto" }}>
            <Flex direction="column" gap="5" style={{ alignItems: "flex-start", flex: "1 0 0", overflow: "auto" }}>
              {filteredNetworks.map((entry, index) => {

                // Guest network (no password)
                if (entry?.x_passphrase === null) {
                  return (
                    <React.Fragment key={index}>
                      <Card style={{ display: "flex", padding: "var(--space-4)", alignSelf: "stretch", flexDirection: "row", 
                        alignItems: "flex-start", gap: "var(--space-7)" }}>
                        {/* Settings block */}
                        <Flex style={{ alignSelf: "stretch", flex: "1 0 0" }} direction="column" align="start" gap="5">
                          <Flex style={{ alignSelf: "stretch" }} direction="column" align="start" gap="2" height="100%">
                            <Flex style={{ alignSelf: "stretch" }} justify="between" align="center">
                              <Flex align="center" gap="2">
                                <Text size="3" weight="medium">{entry?.type}</Text>
                                <Badge size="1" variant="soft" color="gray">Portal</Badge>
                              </Flex>
                              <Switch size="1" variant="surface" defaultChecked={entry?.enabled} onCheckedChange={(checked) => sendPayload(checked, index)} />
                            </Flex>
                            <Text style={{ alignSelf: "stretch", color: "var(--gray-11)" }} size="1" weight="regular">
                              {entry?.description}
                            </Text>
                          </Flex>
                          <Flex style={{ alignSelf: "stretch" }} direction="column" align="start" gap="5" height="100%">
                            <Flex style={{ alignSelf: "stretch" }} direction="column" align="end" gap="4">
                              <label className="text-field" style={{ display: "flex", flexDirection: "column", 
                                alignItems: "flex-start", gap: "var(--space-1)", alignSelf: "stretch" }}>
                                <Text size="2" weight="medium">Network name</Text>
                                <Flex gap="4" style={{ alignSelf: "stretch", alignItems: "flex-end", justifyContent: "center" }}>
                                  <TextField.Root
                                    name="name"
                                    style={{ width: "50%", alignSelf: "stretch" }}
                                    defaultValue={entry.name}
                                    placeholder="Enter network name"
                                    onChange={(event) => handleChanges(event, index)}
                                    size="2"
                                    disabled={!entry.enabled}
                                  />
                                  <Button size="2" variant="surface" color="gray" 
                                    onClick={() => { isBrand 
                                                      ? navigate(`/portal-brand/${brandId}`, 
                                                          {state: {
                                                            isBrand: true, 
                                                            brandId: brandId, 
                                                            brandName: brandName, 
                                                            brandLocationCount: getBrandLocations(brandId, wirelessLanData?.data).length}}) 
                                                      : navigate(`/portal-location/${locationId}`,
                                                          {state: {
                                                            isBrand: false, 
                                                            locationName: locationName, 
                                                            locationAddress: locationsData?.find((loc) => loc.id === locationId)?.address}}
                                                      ) }}
                                    style={{ width: "50%" }}>
                                    Edit portal design
                                  </Button>
                                </Flex>
                              </label>
                              <Dialog.Root>
                                <Dialog.Trigger>
                                  <Button size="2" variant="solid" disabled={interfaceState[index]?.disabled} style={{ width: "100%" }}>
                                    Save changes
                                  </Button>
                                </Dialog.Trigger>
                                <Dialog.Content width="1280px">
                                  <Dialog.Title>Adjust guest network</Dialog.Title>
                                  <Dialog.Description size="2" mb="4">
                                    Before changing the{" "}
                                    <Text size="2" weight="bold">
                                      Network name
                                    </Text>{" "}
                                    or{" "}
                                    <Text size="2" weight="bold">
                                      Password
                                    </Text>
                                    , keep in mind:
                                  </Dialog.Description>
                                  <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                    <Text style={{ padding: "0px var(--space-5)" }} size="1" weight="regular">
                                      <ul style={{ listStyleType: "disc", alignItems: "center" }}>
                                        <li>A new QR code will be generated.</li>
                                        <li>Previously shared QR codes will stop working.</li>
                                        <li>Returning users will need to sign in again via the portal to access the network.</li>
                                        <li>Users currently connected to this network will be disconnected.</li>
                                        <li>The update takes effect immediately.</li>
                                      </ul>
                                    </Text>
                                    <Flex
                                      className="checkbox-container"
                                      style={{ borderRadius: "var(--space-1)", border: "1px solid var(--gray-a3)", background: "var(--color-panel-solid)" }}
                                      display="flex"
                                      width="552px"
                                      p="4"
                                      align="start"
                                      gap="2"
                                    >
                                      <Text as="label" size="2" weight="regular">
                                        <Flex as="span" gap="2">
                                          <Checkbox checked={wifiDisclaimer} onCheckedChange={(checked) => setWifiDisclaimer(checked)} size="2" variant="surface" /> I understand that changing the
                                          network name or password will affect QR codes, guest access, and device connections.
                                        </Flex>
                                      </Text>
                                    </Flex>
                                  </Flex>
                                  <Flex gap="3" mt="4" justify="end">
                                    <Dialog.Close
                                      onClick={() => {
                                        setWifiDisclaimer(false);
                                      }}
                                    >
                                      <Button variant="soft" color="gray">
                                        Cancel
                                      </Button>
                                    </Dialog.Close>
                                    <Dialog.Close disabled={!wifiDisclaimer} onClick={(event) => sendPayload(event, index)}>
                                      <Button variant="solid">Save changes</Button>
                                    </Dialog.Close>
                                  </Flex>
                                </Dialog.Content>
                              </Dialog.Root>
                            </Flex>
                          </Flex>
                        </Flex>
                        {/* QR code block */}
                        <Flex p="4" gap="4" className="qr-code-card"
                          style={{ justifyContent: "center", alignItems: "center", flex: "1 0 0", alignSelf: "stretch",
                            borderRadius: "var(--space-1)", border: "1px solid var(--gray-a6)",
                            background: "var(--color-panel-solid)" }}>
                          <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                            {interfaceState[index]?.disabled ? (
                              <img
                                alt="QR code of guest wifi"
                                src={getQrUrl(`WIFI:T:nopass;S:${entry?.name};`)}
                                style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                              />
                            ) : (
                              <div style={{ width: "128px", height: "128px", background: "var(--gray-a3)", borderRadius: "var(--space-1)" }}></div>
                            )}
                          </Flex>
                          <Flex className="qr-code-options" display="flex" direction="column" justify="center" align="start" gap="2">
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger>
                                <IconButton size="2" variant="surface" color="gray">
                                  <DownloadIcon width="16" height="16" />
                                </IconButton>
                              </DropdownMenu.Trigger>
                              <DropdownMenu.Content side="right">
                                <DropdownMenu.Item
                                  onSelect={handleDownload(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=ffffff&color=000000&format=png`,
                                    `${entry?.name}_qr_code.png`
                                  )}
                                >
                                  Download PNG in black
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  onSelect={handleDownload(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=000000&color=ffffff&format=png`,
                                    `${entry?.name}_qr_code.png`
                                  )}
                                >
                                  Download PNG in white
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                                  <Dialog.Root>
                                    <Dialog.Trigger>
                                      <Text size="2" weight="regular">
                                        Download PNG in colour...
                                      </Text>
                                    </Dialog.Trigger>
                                    <Dialog.Content width="1280px">
                                      <Dialog.Title>Download PNG in colour</Dialog.Title>
                                      <Dialog.Description size="2" mb="4">
                                        Choose a colour for your QR code
                                      </Dialog.Description>
                                      <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                        <div
                                          className="qr-code-card"
                                          style={{
                                            display: "flex",
                                            padding: "var(--space-4)",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: "var(--space-4)",
                                            flex: "1 0 0",
                                            alignSelf: "stretch",
                                            borderRadius: "var(--space-1)",
                                            border: "1px dashed var(--gray-a6)",
                                            background: "var(--color-panel-solid)",
                                          }}
                                        >
                                          <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                                            <img
                                              alt="QR code of guest wifi"
                                              src={`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};P:${
                                                entry?.x_passphrase
                                              };;&size=128x128&bgcolor=${colourSelection}&color=${getQrForegroundColor()}&format=png`}
                                              style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                            />
                                          </Flex>
                                        </div>
                                        <Flex className="qr-code-color-picker" style={{ alignSelf: "stretch" }} display="flex" align="center" gap="2">
                                          <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                            <input
                                              type="color"
                                              id="button_colour_picker"
                                              value={`#${colourSelection}`}
                                              onChange={handleColourField}
                                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                            />
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: `#${colourSelection}`,
                                                borderRadius: "var(--radius-2)",
                                                border: "1px solid var(--gray-a6)",
                                              }}
                                            ></div>
                                          </label>
                                          <TextField.Root onChange={handleColourField} style={{ width: "100%" }} defaultValue={`#${colourSelection}`} placeholder="Enter hex colour code" size="2" />
                                        </Flex>
                                      </Flex>
                                      <Flex gap="3" mt="4" justify="end">
                                        <Dialog.Close onClick={() => setColourSelection("666666")}>
                                          <Button variant="soft" color="gray">
                                            Cancel
                                          </Button>
                                        </Dialog.Close>
                                        <Dialog.Close
                                          onClick={handleDownload(
                                            `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};P:${
                                              entry?.x_passphrase
                                            };;&size=128x128&bgcolor=${getQrBackgroundColor()}&color=${colourSelection}&format=png`,
                                            `${entry?.name}_qr_code.png`
                                          )}
                                        >
                                          <Button>Download</Button>
                                        </Dialog.Close>
                                      </Flex>
                                    </Dialog.Content>
                                  </Dialog.Root>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  onSelect={handleDownload(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=1a191c&color=666666&format=svg`,
                                    `${entry?.name}_qr_code.svg`
                                  )}
                                >
                                  Download SVG
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Root>
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger>
                                <IconButton size="2" variant="surface" color="gray">
                                  <CopyIcon width="16" height="16" />
                                </IconButton>
                              </DropdownMenu.Trigger>
                              <DropdownMenu.Content side="right">
                                <DropdownMenu.Item
                                  onSelect={handleCopy(`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=ffffff&color=000000&format=png`)}
                                >
                                  Copy PNG in black
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  onSelect={handleCopy(`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=000000&color=ffffff&format=png`)}
                                >
                                  Copy PNG in white
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                                  <Dialog.Root>
                                    <Dialog.Trigger>
                                      <Text size="2" weight="regular">
                                        Copy PNG in colour...
                                      </Text>
                                    </Dialog.Trigger>
                                    <Dialog.Content width="1280px">
                                      <Dialog.Title>Copy PNG in colour</Dialog.Title>
                                      <Dialog.Description size="2" mb="4">
                                        Choose a colour for your QR code
                                      </Dialog.Description>
                                      <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                        <div
                                          className="qr-code-card"
                                          style={{
                                            display: "flex",
                                            padding: "var(--space-4)",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: "var(--space-4)",
                                            flexGrow: "1",
                                            alignSelf: "stretch",
                                            borderRadius: "var(--space-1)",
                                            border: "1px dashed var(--gray-a6)",
                                            background: "var(--color-panel-solid)",
                                          }}
                                        >
                                          <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                                            <img
                                              alt="QR code of guest wifi"
                                              src={`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`}
                                              style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                            />
                                          </Flex>
                                        </div>
                                        <Flex className="qr-code-color-picker" style={{ alignSelf: "stretch" }} display="flex" align="center" gap="2">
                                          <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                            <input
                                              type="color"
                                              id="button_colour_picker"
                                              value={`#${colourSelection}`}
                                              onChange={handleColourField}
                                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                            />
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: `#${colourSelection}`,
                                                borderRadius: "var(--radius-2)",
                                                border: "1px solid var(--gray-a6)",
                                              }}
                                            ></div>
                                          </label>
                                          <TextField.Root onChange={handleColourField} style={{ width: "100%" }} defaultValue={`#${colourSelection}`} placeholder="Enter hex colour code" size="2" />
                                        </Flex>
                                      </Flex>
                                      <Flex gap="3" mt="4" justify="end">
                                        <Dialog.Close onClick={() => setColourSelection("666666")}>
                                          <Button variant="soft" color="gray">
                                            Cancel
                                          </Button>
                                        </Dialog.Close>
                                        <Dialog.Close
                                          onClick={handleCopy(
                                            `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:nopass;S:${entry?.name};;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`
                                          )}
                                        >
                                          <Button>Copy</Button>
                                        </Dialog.Close>
                                      </Flex>
                                    </Dialog.Content>
                                  </Dialog.Root>
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Root>
                          </Flex>
                        </Flex>
                      </Card>
                    </React.Fragment>
                  );
                }

                // Staff Wi-Fi Network with custom password management
                else if (entry?.type === "Staff Wi-Fi Network") {
                  return (
                    <React.Fragment key={index}>
                      <Card style={{ display: "flex", padding: "var(--space-4)", alignSelf: "stretch", flexDirection: "row", 
                        alignItems: "flex-start", gap: "var(--space-7)" }}>
                        {/* Settings block */}
                        <Flex style={{ alignSelf: "stretch", flex: "1 0 0" }} direction="column" align="start" gap="5">
                          <Flex style={{ alignSelf: "stretch" }} direction="column" align="start" gap="2">
                            <Flex style={{ alignSelf: "stretch" }} justify="between" align="center">
                              <Flex align="center" gap="2">
                                <Text size="3" weight="medium">
                                  {entry?.type}
                                </Text>
                              </Flex>
                              <Switch size="1" variant="surface" defaultChecked={entry?.enabled} onCheckedChange={(checked) => sendPayload(checked, index)} />
                            </Flex>
                            <Text style={{ alignSelf: "stretch", color: "var(--gray-11)" }} size="1" weight="regular">
                              {entry?.description}
                            </Text>
                          </Flex>
                          <Flex style={{ alignSelf: "stretch" }} direction="column" align="stretch" gap="5">
                            {/* Network name field - full width at top */}

                            {/* For brand view with staff network - show locations/passwords */}
                            {isBrand && brandId ? (
                              <>
                                {/* For brand view - keep name at the top */}
                                <label className="text-field" style={{ flexDirection: "column", gap: "var(--space-1)" }}>
                                  <Text size="2" weight="medium">
                                    Network name
                                  </Text>
                                  <TextField.Root
                                    name="name"
                                    style={{ width: "100%" }}
                                    defaultValue={entry.name}
                                    placeholder="Enter network name"
                                    onChange={(event) => handleChanges(event, index)}
                                    size="2"
                                    disabled={!entry.enabled}
                                  />
                                </label>

                                <Flex direction="column" gap="2" style={{ maxHeight: "200px", overflowY: "auto", 
                                  overflowX: 'hidden' }}>
                                  {/* Header row */}
                                  <Flex>
                                    <Text size="1" weight="medium" style={{ width: "50%" }}>Location</Text>
                                    <Text size="1" weight="medium" style={{ width: "50%" }}>Password</Text>
                                  </Flex>
                                  <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                                  {/* Location password rows */}
                                  {getBrandLocations(brandId, wirelessLanData?.data).length === 0 ? (
                                    <Flex p="2" style={{ justifyContent: 'center', alignItems: 'center', 
                                      alignSelf: 'stretch', width: '100%', borderRadius: 'var(--radius-4)',
                                      border: '1px dashed var(--gray-a6)' }}>
                                        <Text size="1" weight="medium" color="gray">No locations</Text>
                                    </Flex>
                                  ) : null}
                                    {getBrandLocations(brandId, wirelessLanData?.data).map((location, i, arr) => (
                                    <React.Fragment key={location.id}>
                                      <Flex>
                                        <Text size="1" style={{ width: "50%", display: "flex", alignItems: "center" }}>
                                          {location.name}
                                        </Text>
                                        <TextField.Root
                                          name={`password-${location.id}`}
                                          style={{
                                            width: "50%",

                                            boxSizing: "border-box",
                                          }}
                                          type={interfaceState[index]?.textfield_type}
                                          onFocus={(event) => handleChanges(event, index)}
                                          onBlur={(event) => handleChanges(event, index)}
                                          defaultValue={getLocationPassword(entry, location.id)}
                                          placeholder="Enter password"
                                          onChange={(event) => handlePasswordChange(event, index, location.id)}
                                          size="1"
                                          disabled={!entry.enabled}
                                        />
                                      </Flex>
                                      {/* Add separator except after the last row */}
                                      {i < arr.length - 1 && 
                                        <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                                      }
                                    </React.Fragment>
                                  ))}
                                </Flex>
                              </>
                            ) : (
                              // For independent location - match the Work Wi-Fi layout with name and password side by side
                              <Flex className="frame-528172863" gap="4">
                                <label className="text-field" style={{ flexDirection: "column", gap: "var(--space-1)", width: "50%" }}>
                                  <Text size="2" weight="medium">
                                    Network name
                                  </Text>
                                  <TextField.Root
                                    name="name"
                                    style={{ alignSelf: "stretch" }}
                                    defaultValue={entry.name}
                                    placeholder="Enter network name"
                                    onChange={(event) => handleChanges(event, index)}
                                    size="2"
                                    disabled={!entry.enabled}
                                  />
                                </label>
                                <label className="text-field" style={{ flexDirection: "column", gap: "var(--space-1)", width: "50%" }}>
                                  <Text size="2" weight="medium">
                                    Password
                                  </Text>
                                  <TextField.Root
                                    name="x_passphrase"
                                    style={{ alignSelf: "stretch" }}
                                    type={interfaceState[index]?.textfield_type}
                                    onFocus={(event) => handleChanges(event, index)}
                                    onBlur={(event) => handleChanges(event, index)}
                                    defaultValue={entry?.x_passphrase}
                                    placeholder="Enter password"
                                    onChange={(event) => handleChanges(event, index)}
                                    size="2"
                                    disabled={!entry.enabled}
                                  />
                                </label>
                              </Flex>
                            )}

                            <Flex className="frame-528172774" align="end">
                              <Dialog.Root>
                                <Dialog.Trigger>
                                  <Button size="2" variant="solid" disabled={interfaceState[index]?.disabled} style={{ width: "100%" }}>
                                    Save changes
                                  </Button>
                                </Dialog.Trigger>
                                <Dialog.Content width="1280px">
                                  <Dialog.Title>Adjust Staff Network</Dialog.Title>
                                  <Dialog.Description size="2" mb="4">
                                    Before changing the{" "}
                                    <Text size="2" weight="bold">
                                      Network name
                                    </Text>{" "}
                                    or{" "}
                                    <Text size="2" weight="bold">
                                      Password
                                    </Text>
                                    , keep in mind:
                                  </Dialog.Description>
                                  <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                    <Text style={{ padding: "0px var(--space-5)" }} size="1" weight="regular">
                                      <ul style={{ listStyleType: "disc", alignItems: "center" }}>
                                        <li>A new QR code will be generated.</li>
                                        <li>Previously shared QR codes will stop working.</li>
                                        <li>Users currently connected to this network will be disconnected.</li>
                                        <li>The update takes effect immediately.</li>
                                      </ul>
                                    </Text>
                                    <Flex
                                      className="checkbox-container"
                                      style={{ borderRadius: "var(--space-1)", border: "1px solid var(--gray-a3)", background: "var(--color-panel-solid)" }}
                                      display="flex"
                                      width="552px"
                                      p="4"
                                      align="start"
                                      gap="2"
                                    >
                                      <Text as="label" size="2" weight="regular">
                                        <Flex as="span" gap="2">
                                          <Checkbox checked={wifiDisclaimer} onCheckedChange={(checked) => setWifiDisclaimer(checked)} size="2" variant="surface" /> I understand that changing the
                                          network name or password will affect QR codes, guest access, and device connections.
                                        </Flex>
                                      </Text>
                                    </Flex>
                                  </Flex>
                                  <Flex gap="3" mt="4" justify="end">
                                    <Dialog.Close
                                      onClick={() => {
                                        setWifiDisclaimer(false);
                                      }}
                                    >
                                      <Button variant="soft" color="gray">
                                        Cancel
                                      </Button>
                                    </Dialog.Close>
                                    <Dialog.Close disabled={!wifiDisclaimer} onClick={(event) => sendPayload(event, index)}>
                                      <Button variant="solid">Save changes</Button>
                                    </Dialog.Close>
                                  </Flex>
                                </Dialog.Content>
                              </Dialog.Root>
                            </Flex>
                          </Flex>
                        </Flex>
                        {/* QR code block */}
                        <Flex className="qr-code-card" p="4" gap="4"
                          style={{ justifyContent: "center", alignItems: "center", flex: "1 0 0", alignSelf: "stretch",
                            borderRadius: "var(--space-1)", border: "1px solid var(--gray-a6)",
                            background: "var(--color-panel-solid)" }}>
                          <Flex gap="2" direction="column" style={{ justifyContent: "center", alignItems: "center" }}>
                            <Flex className="qr-code-wifi" width="148px" height="148px" justify="center" align="center" gap="2">
                              {(interfaceState[index]?.disabled) && ((isBrand ? getBrandLocations(brandId, wirelessLanData?.data).length > 0 : true) ) ? (
                                <img
                                  alt="QR code of staff wifi"
                                  src={getQrUrl(`WIFI:T:WPA;S:${entry?.name};P:${isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase};`)}
                                  style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                />
                              ) : (
                                <div style={{ width: "128px", height: "128px", background: "var(--gray-a3)", borderRadius: "var(--space-1)" }}></div>
                              )}
                            </Flex>
                            {/* Location selector for brand view - moved below QR code */}
                            {isBrand && brandId && (getBrandLocations(brandId, wirelessLanData?.data).length > 0) && (
                              <Flex direction="column">
                                <Select.Root value={selectedLocationId || ""} onValueChange={handleLocationChange} size="2" width="100%">
                                  <Select.Trigger>{getBrandLocations(brandId, wirelessLanData?.data).find((loc) => loc.id === selectedLocationId)?.name || "Select location"}</Select.Trigger>
                                  <Select.Content >
                                    {getBrandLocations(brandId, wirelessLanData?.data).map((location) => (
                                      <Select.Item key={location.id} value={location.id}>
                                        {location.name}
                                      </Select.Item>
                                    ))}
                                  </Select.Content>
                                </Select.Root>
                              </Flex>
                            )}
                          </Flex>
                          { ((isBrand ? getBrandLocations(brandId, wirelessLanData?.data).length > 0 : true) ) && (
                            <Flex className="qr-code-options" direction="column" justify="center" align="start" gap="2">
                              <DropdownMenu.Root>
                                <DropdownMenu.Trigger>
                                  <IconButton size="2" variant="surface" color="gray">
                                    <DownloadIcon width="16" height="16" />
                                  </IconButton>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content side="right">
                                  <DropdownMenu.Item
                                    onSelect={handleDownload(
                                      `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                        isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                      };;&size=128x128&bgcolor=ffffff&color=000000&format=png`,
                                      `${entry?.name}_qr_code.png`
                                    )}
                                  >
                                    Download PNG in black
                                  </DropdownMenu.Item>
                                  <DropdownMenu.Item
                                    onSelect={handleDownload(
                                      `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                        isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                      };;&size=128x128&bgcolor=000000&color=ffffff&format=png`,
                                      `${entry?.name}_qr_code.png`
                                    )}
                                  >
                                    Download PNG in white
                                  </DropdownMenu.Item>
                                  <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                                    <Dialog.Root>
                                      <Dialog.Trigger>
                                        <Text size="2" weight="regular">
                                          Download PNG in colour...
                                        </Text>
                                      </Dialog.Trigger>
                                      <Dialog.Content width="1280px">
                                        <Dialog.Title>Download PNG in colour</Dialog.Title>
                                        <Dialog.Description size="2" mb="4">
                                          Choose a colour for your QR code
                                        </Dialog.Description>
                                        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                          <div
                                            className="qr-code-card"
                                            style={{
                                              display: "flex",
                                              padding: "var(--space-4)",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              gap: "var(--space-4)",
                                              flex: "1 0 0",
                                              alignSelf: "stretch",
                                              borderRadius: "var(--space-1)",
                                              border: "1px dashed var(--gray-a6)",
                                              background: "var(--color-panel-solid)",
                                            }}
                                          >
                                            <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                                              <img
                                                alt="QR code of staff wifi"
                                                src={`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                                  isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                                };;&size=128x128&bgcolor=${colourSelection}&color=${getQrForegroundColor()}&format=png`}
                                                style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                              />
                                            </Flex>
                                          </div>
                                          <Flex className="qr-code-color-picker" style={{ alignSelf: "stretch" }} display="flex" align="center" gap="2">
                                            <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                              <input
                                                type="color"
                                                id="button_colour_picker"
                                                value={`#${colourSelection}`}
                                                onChange={handleColourField}
                                                style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                              />
                                              <div
                                                style={{
                                                  width: "32px",
                                                  height: "32px",
                                                  backgroundColor: `#${colourSelection}`,
                                                  borderRadius: "var(--radius-2)",
                                                  border: "1px solid var(--gray-a6)",
                                                }}
                                              ></div>
                                            </label>
                                            <TextField.Root onChange={handleColourField} style={{ width: "100%" }} defaultValue={`#${colourSelection}`} placeholder="Enter hex colour code" size="2" />
                                          </Flex>
                                        </Flex>
                                        <Flex gap="3" mt="4" justify="end">
                                          <Dialog.Close onClick={() => setColourSelection("666666")}>
                                            <Button variant="soft" color="gray">
                                              Cancel
                                            </Button>
                                          </Dialog.Close>
                                          <Dialog.Close
                                            onClick={handleDownload(
                                              `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                                isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                              };;&size=128x128&bgcolor=${getQrBackgroundColor()}&color=${colourSelection}&format=png`,
                                              `${entry?.name}_qr_code.png`
                                            )}
                                          >
                                            <Button>Download</Button>
                                          </Dialog.Close>
                                        </Flex>
                                      </Dialog.Content>
                                    </Dialog.Root>
                                  </DropdownMenu.Item>
                                  <DropdownMenu.Item
                                    onSelect={handleDownload(
                                      `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                        isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                      };;&size=128x128&bgcolor=1a191c&color=666666&format=svg`,
                                      `${entry?.name}_qr_code.svg`
                                    )}
                                  >
                                    Download SVG
                                  </DropdownMenu.Item>
                                </DropdownMenu.Content>
                              </DropdownMenu.Root>
                              <DropdownMenu.Root>
                                <DropdownMenu.Trigger>
                                  <IconButton size="2" variant="surface" color="gray">
                                    <CopyIcon width="16" height="16" />
                                  </IconButton>
                                </DropdownMenu.Trigger>
                                <DropdownMenu.Content side="right">
                                  <DropdownMenu.Item
                                    onSelect={handleCopy(
                                      `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                        isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                      };;&size=128x128&bgcolor=ffffff&color=000000&format=png`
                                    )}
                                  >
                                    Copy PNG in black
                                  </DropdownMenu.Item>
                                  <DropdownMenu.Item
                                    onSelect={handleCopy(
                                      `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                        isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                      };;&size=128x128&bgcolor=000000&color=ffffff&format=png`
                                    )}
                                  >
                                    Copy PNG in white
                                  </DropdownMenu.Item>
                                  <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                                    <Dialog.Root>
                                      <Dialog.Trigger>
                                        <Text size="2" weight="regular">
                                          Copy PNG in colour...
                                        </Text>
                                      </Dialog.Trigger>
                                      <Dialog.Content width="1280px">
                                        <Dialog.Title>Copy PNG in colour</Dialog.Title>
                                        <Dialog.Description size="2" mb="4">
                                          Choose a colour for your QR code
                                        </Dialog.Description>
                                        <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                          <div
                                            className="qr-code-card"
                                            style={{
                                              display: "flex",
                                              padding: "var(--space-4)",
                                              justifyContent: "center",
                                              alignItems: "center",
                                              gap: "var(--space-4)",
                                              flex: "1 0 0",
                                              alignSelf: "stretch",
                                              borderRadius: "var(--space-1)",
                                              border: "1px dashed var(--gray-a6)",
                                              background: "var(--color-panel-solid)",
                                            }}
                                          >
                                            <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                                              <img
                                                alt="QR code of staff wifi"
                                                src={`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                                  isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                                };;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`}
                                                style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                              />
                                            </Flex>
                                          </div>
                                          <Flex className="qr-code-color-picker" style={{ alignSelf: "stretch" }} display="flex" align="center" gap="2">
                                            <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                              <input
                                                type="color"
                                                id="button_colour_picker"
                                                value={`#${colourSelection}`}
                                                onChange={handleColourField}
                                                style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                              />
                                              <div
                                                style={{
                                                  width: "32px",
                                                  height: "32px",
                                                  backgroundColor: `#${colourSelection}`,
                                                  borderRadius: "var(--radius-2)",
                                                  border: "1px solid var(--gray-a6)",
                                                }}
                                              ></div>
                                            </label>
                                            <TextField.Root onChange={handleColourField} style={{ width: "100%" }} defaultValue={`#${colourSelection}`} placeholder="Enter hex colour code" size="2" />
                                          </Flex>
                                        </Flex>
                                        <Flex gap="3" mt="4" justify="end">
                                          <Dialog.Close onClick={() => setColourSelection("666666")}>
                                            <Button variant="soft" color="gray">
                                              Cancel
                                            </Button>
                                          </Dialog.Close>
                                          <Dialog.Close
                                            onClick={handleCopy(
                                              `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                                isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                              };;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`
                                            )}
                                          >
                                            <Button>Copy</Button>
                                          </Dialog.Close>
                                        </Flex>
                                      </Dialog.Content>
                                    </Dialog.Root>
                                  </DropdownMenu.Item>
                                </DropdownMenu.Content>
                              </DropdownMenu.Root>
                            </Flex>)}
                        </Flex>
                      </Card>
                    </React.Fragment>
                  );
                }

                // Work Wi-Fi Network (keep existing layout)
                else {
                  return (
                    <React.Fragment key={index}>
                      <Card style={{ display: "flex", padding: "var(--space-4)", alignSelf: "stretch", flexDirection: "row", 
                        alignItems: "flex-start", gap: "var(--space-7)" }}>
                        {/* Settings block */}
                        <Flex style={{ alignSelf: "stretch", flex: "1 0 0" }} direction="column" align="start" gap="5">
                          <Flex style={{ alignSelf: "stretch" }} direction="column" align="start" gap="2">
                            <Flex style={{ alignSelf: "stretch" }} justify="between" align="center">
                              <Flex display="flex" align="center" gap="2">
                                <Text size="3" weight="medium">
                                  {entry?.type}
                                </Text>
                                {entry?.type === "Work Wi-Fi Network" && (
                                  <Badge size="1" variant="soft" color="gray">
                                    Portal
                                  </Badge>
                                )}
                              </Flex>
                              <Switch size="1" variant="surface" defaultChecked={entry?.enabled} onCheckedChange={(checked) => sendPayload(checked, index)} />
                            </Flex>
                            <Text style={{ alignSelf: "stretch", color: "var(--gray-11)" }} size="1" weight="regular">
                              {entry?.description}
                            </Text>
                          </Flex>
                          <Flex className="frame-528172771" style={{ alignSelf: "stretch" }} display="flex" direction="column" align="stretch" gap="5">
                            <Flex className="frame-528172863" gap="4">
                              <label className="text-field" style={{ flexDirection: "column", gap: "var(--space-1)", width: "50%" }}>
                                <Text size="2" weight="medium">
                                  Network name
                                </Text>
                                <TextField.Root
                                  name="name"
                                  style={{
                                    alignSelf: "stretch",
                                  }}
                                  defaultValue={entry.name}
                                  placeholder="Enter network name"
                                  onChange={(event) => handleChanges(event, index)}
                                  size="2"
                                  disabled={!entry.enabled}
                                />
                              </label>
                              <label className="text-field" style={{ flexDirection: "column", gap: "var(--space-1)", width: "50%" }}>
                                <Text size="2" weight="medium">
                                  Password
                                </Text>
                                <TextField.Root
                                  name="x_passphrase"
                                  style={{
                                    alignSelf: "stretch",
                                  }}
                                  type={interfaceState[index]?.textfield_type}
                                  onFocus={(event) => handleChanges(event, index)}
                                  onBlur={(event) => handleChanges(event, index)}
                                  defaultValue={entry?.x_passphrase}
                                  placeholder="Enter password"
                                  onChange={(event) => handleChanges(event, index)}
                                  size="2"
                                  disabled={!entry.enabled}
                                />
                              </label>
                            </Flex>
                            <Flex className="frame-528172774" style={{ alignSelf: "stretch" }} display="flex" direction="column" align="end" gap="4">
                              <Dialog.Root>
                                <Dialog.Trigger>
                                  <Button size="2" variant="solid" disabled={interfaceState[index]?.disabled} style={{ width: "100%" }}>
                                    Save changes
                                  </Button>
                                </Dialog.Trigger>
                                <Dialog.Content width="1280px">
                                  <Dialog.Title>Adjust Work Network</Dialog.Title>
                                  <Dialog.Description size="2" mb="4">
                                    Before changing the{" "}
                                    <Text size="2" weight="bold">
                                      Network name
                                    </Text>{" "}
                                    or{" "}
                                    <Text size="2" weight="bold">
                                      Password
                                    </Text>
                                    , keep in mind:
                                  </Dialog.Description>
                                  <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                    <Text style={{ padding: "0px var(--space-5)" }} size="1" weight="regular">
                                      <ul style={{ listStyleType: "disc", alignItems: "center" }}>
                                        <li>A new QR code will be generated.</li>
                                        <li>Previously shared QR codes will stop working.</li>
                                        <li>Returning users will need to sign in again via the portal to access the network.</li>
                                        <li>Users currently connected to this network will be disconnected.</li>
                                        <li>The update takes effect immediately.</li>
                                      </ul>
                                    </Text>
                                    <Flex
                                      className="checkbox-container"
                                      style={{ borderRadius: "var(--space-1)", border: "1px solid var(--gray-a3)", background: "var(--color-panel-solid)" }}
                                      display="flex"
                                      width="552px"
                                      p="4"
                                      align="start"
                                      gap="2"
                                    >
                                      <Text as="label" size="2" weight="regular">
                                        <Flex as="span" gap="2">
                                          <Checkbox checked={wifiDisclaimer} onCheckedChange={(checked) => setWifiDisclaimer(checked)} size="2" variant="surface" /> I understand that changing the
                                          network name or password will affect QR codes, guest access, and device connections.
                                        </Flex>
                                      </Text>
                                    </Flex>
                                  </Flex>
                                  <Flex gap="3" mt="4" justify="end">
                                    <Dialog.Close
                                      onClick={() => {
                                        setWifiDisclaimer(false);
                                      }}
                                    >
                                      <Button variant="soft" color="gray">
                                        Cancel
                                      </Button>
                                    </Dialog.Close>
                                    <Dialog.Close disabled={!wifiDisclaimer} onClick={(event) => sendPayload(event, index)}>
                                      <Button variant="solid">Save changes</Button>
                                    </Dialog.Close>
                                  </Flex>
                                </Dialog.Content>
                              </Dialog.Root>
                            </Flex>
                          </Flex>
                        </Flex>
                        {/* QR code block */}
                        <Flex
                          className="qr-code-card"
                          p="4"
                          gap="4"
                          style={{
                            justifyContent: "center",
                            alignItems: "center",
                            flex: "1 0 0",
                            alignSelf: "stretch",
                            borderRadius: "var(--space-1)",
                            border: "1px solid var(--gray-a6)",
                            background: "var(--color-panel-solid)",
                          }}
                        >
                          <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                            {interfaceState[index]?.disabled ? (
                              <img
                                alt="QR code of work wifi"
                                src={getQrUrl(`WIFI:T:WPA;S:${entry?.name};P:${entry?.x_passphrase};`)}
                                style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                              />
                            ) : (
                              <div style={{ width: "128px", height: "128px", background: "var(--gray-a3)", borderRadius: "var(--space-1)" }}></div>
                            )}
                          </Flex>
                          <Flex className="qr-code-options" display="flex" direction="column" justify="center" align="start" gap="2">
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger>
                                <IconButton size="2" variant="surface" color="gray">
                                  <DownloadIcon width="16" height="16" />
                                </IconButton>
                              </DropdownMenu.Trigger>
                              <DropdownMenu.Content side="right">
                                <DropdownMenu.Item
                                  onSelect={handleDownload(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${entry?.x_passphrase};;&size=128x128&bgcolor=ffffff&color=000000&format=png`,
                                    `${entry?.name}_qr_code.png`
                                  )}
                                >
                                  Download PNG in black
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  onSelect={handleDownload(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${entry?.x_passphrase};;&size=128x128&bgcolor=000000&color=ffffff&format=png`,
                                    `${entry?.name}_qr_code.png`
                                  )}
                                >
                                  Download PNG in white
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                                  <Dialog.Root>
                                    <Dialog.Trigger>
                                      <Text size="2" weight="regular">
                                        Download PNG in colour...
                                      </Text>
                                    </Dialog.Trigger>
                                    <Dialog.Content width="1280px">
                                      <Dialog.Title>Copy PNG in colour</Dialog.Title>
                                      <Dialog.Description size="2" mb="4">
                                        Choose a colour for your QR code
                                      </Dialog.Description>
                                      <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                        <div
                                          className="qr-code-card"
                                          style={{
                                            display: "flex",
                                            padding: "var(--space-4)",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: "var(--space-4)",
                                            flex: "1 0 0",
                                            alignSelf: "stretch",
                                            borderRadius: "var(--space-1)",
                                            border: "1px dashed var(--gray-a6)",
                                            background: "var(--color-panel-solid)",
                                          }}
                                        >
                                          <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                                            <img
                                              alt="QR code of staff wifi"
                                              src={`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                                isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                              };;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`}
                                              style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                            />
                                          </Flex>
                                        </div>
                                        <Flex className="qr-code-color-picker" style={{ alignSelf: "stretch" }} display="flex" align="center" gap="2">
                                          <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                            <input
                                              type="color"
                                              id="button_colour_picker"
                                              value={`#${colourSelection}`}
                                              onChange={handleColourField}
                                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                            />
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: `#${colourSelection}`,
                                                borderRadius: "var(--radius-2)",
                                                border: "1px solid var(--gray-a6)",
                                              }}
                                            ></div>
                                          </label>
                                          <TextField.Root onChange={handleColourField} style={{ width: "100%" }} defaultValue={`#${colourSelection}`} placeholder="Enter hex colour code" size="2" />
                                        </Flex>
                                      </Flex>
                                      <Flex gap="3" mt="4" justify="end">
                                        <Dialog.Close onClick={() => setColourSelection("666666")}>
                                          <Button variant="soft" color="gray">
                                            Cancel
                                          </Button>
                                        </Dialog.Close>
                                        <Dialog.Close
                                          onClick={handleCopy(
                                            `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                              isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                            };;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`
                                          )}
                                        >
                                          <Button>Copy</Button>
                                        </Dialog.Close>
                                      </Flex>
                                    </Dialog.Content>
                                  </Dialog.Root>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  onSelect={handleDownload(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${entry?.x_passphrase};;&size=128x128&bgcolor=1a191c&color=666666&format=svg`,
                                    `${entry?.name}_qr_code.svg`
                                  )}
                                >
                                  Download SVG
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Root>
                            <DropdownMenu.Root>
                              <DropdownMenu.Trigger>
                                <IconButton size="2" variant="surface" color="gray">
                                  <CopyIcon width="16" height="16" />
                                </IconButton>
                              </DropdownMenu.Trigger>
                              <DropdownMenu.Content side="right">
                                <DropdownMenu.Item
                                  onSelect={handleCopy(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                      isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                    };;&size=128x128&bgcolor=ffffff&color=000000&format=png`
                                  )}
                                >
                                  Copy PNG in black
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                  onSelect={handleCopy(
                                    `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                      isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                    };;&size=128x128&bgcolor=000000&color=ffffff&format=png`
                                  )}
                                >
                                  Copy PNG in white
                                </DropdownMenu.Item>
                                <DropdownMenu.Item onSelect={(event) => event.preventDefault()}>
                                  <Dialog.Root>
                                    <Dialog.Trigger>
                                      <Text size="2" weight="regular">
                                        Copy PNG in colour...
                                      </Text>
                                    </Dialog.Trigger>
                                    <Dialog.Content width="1280px">
                                      <Dialog.Title>Copy PNG in colour</Dialog.Title>
                                      <Dialog.Description size="2" mb="4">
                                        Choose a colour for your QR code
                                      </Dialog.Description>
                                      <Flex style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start" gap="5">
                                        <div
                                          className="qr-code-card"
                                          style={{
                                            display: "flex",
                                            padding: "var(--space-4)",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: "var(--space-4)",
                                            flex: "1 0 0",
                                            alignSelf: "stretch",
                                            borderRadius: "var(--space-1)",
                                            border: "1px dashed var(--gray-a6)",
                                            background: "var(--color-panel-solid)",
                                          }}
                                        >
                                          <Flex className="qr-code-wifi" display="flex" width="148px" height="148px" justify="center" align="center" gap="2">
                                            <img
                                              alt="QR code of staff wifi"
                                              src={`https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                                isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                              };;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`}
                                              style={{ display: "flex", width: "128px", height: "128px", justifyContent: "center", alignItems: "center", gap: "var(--space-2)" }}
                                            />
                                          </Flex>
                                        </div>
                                        <Flex className="qr-code-color-picker" style={{ alignSelf: "stretch" }} display="flex" align="center" gap="2">
                                          <label htmlFor="button_colour_picker" style={{ position: "relative", display: "inline-block" }}>
                                            <input
                                              type="color"
                                              id="button_colour_picker"
                                              value={`#${colourSelection}`}
                                              onChange={handleColourField}
                                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0 }}
                                            />
                                            <div
                                              style={{
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: `#${colourSelection}`,
                                                borderRadius: "var(--radius-2)",
                                                border: "1px solid var(--gray-a6)",
                                              }}
                                            ></div>
                                          </label>
                                          <TextField.Root onChange={handleColourField} style={{ width: "100%" }} defaultValue={`#${colourSelection}`} placeholder="Enter hex colour code" size="2" />
                                        </Flex>
                                      </Flex>
                                      <Flex gap="3" mt="4" justify="end">
                                        <Dialog.Close onClick={() => setColourSelection("666666")}>
                                          <Button variant="soft" color="gray">
                                            Cancel
                                          </Button>
                                        </Dialog.Close>
                                        <Dialog.Close
                                          onClick={handleCopy(
                                            `https://api.qrserver.com/v1/create-qr-code/?data=WIFI:T:WPA;S:${entry?.name};P:${
                                              isBrand && selectedLocationId ? getLocationPassword(entry, selectedLocationId) : entry?.x_passphrase
                                            };;&size=128x128&bgcolor=1a191c&color=${colourSelection}&format=png`
                                          )}
                                        >
                                          <Button>Copy</Button>
                                        </Dialog.Close>
                                      </Flex>
                                    </Dialog.Content>
                                  </Dialog.Root>
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Root>
                          </Flex>
                        </Flex>
                      </Card>
                    </React.Fragment>
                  );
                }
              })}
            </Flex>
          </Flex>
        </React.Fragment>
      )}
      <ToastAlert />
    </Flex>
  );
};

export default Networks;
